/**
 * Lightweight Face Detector using Canvas + Viola-Jones-like skin detection
 * No external model files needed - works with pure JavaScript
 * Falls back to simple motion/skin detection heuristics
 */

export const detectFaceInCanvas = (canvas) => {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  // Simple skin-color detection heuristic (works well for frontal faces)
  // Based on RGB skin color ranges
  let skinPixels = [];
  const width = canvas.width;
  const height = canvas.height;
  
  // Divide image into grid cells and count skin pixels per cell
  const gridSize = 8;
  const cellW = Math.floor(width / gridSize);
  const cellH = Math.floor(height / gridSize);
  const skinGrid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(0));
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      // Multiple skin color rules
      const isSkin = (
        // Rule 1: RGB range
        (r > 95 && g > 40 && b > 20 && r > g && r > b && (r - g) > 15 && (r - b) > 15) ||
        // Rule 2: HSV-like (for varied lighting)
        (r > 200 && g > 150 && b > 100 && Math.abs(r - g) < 50 && r > b) ||
        // Rule 3: Lighter skin tones
        (r > 220 && g > 180 && b > 140 && r > b)
      );
      
      if (isSkin) {
        const cellX = Math.min(gridSize - 1, Math.floor(x / cellW));
        const cellY = Math.min(gridSize - 1, Math.floor(y / cellH));
        skinGrid[cellY][cellX]++;
      }
    }
  }
  
  // Find the region with most skin pixels (likely face area)
  let maxSkin = 0;
  let centerX = width / 2;
  let centerY = height / 2;
  let totalSkin = 0;
  
  for (let cy = 0; cy < gridSize; cy++) {
    for (let cx = 0; cx < gridSize; cx++) {
      const count = skinGrid[cy][cx];
      totalSkin += count;
      if (count > maxSkin) {
        maxSkin = count;
        centerX = (cx + 0.5) * cellW;
        centerY = (cy + 0.5) * cellH;
      }
    }
  }
  
  // Calculate confidence based on skin concentration in center region
  const totalPixels = width * height;
  const skinRatio = totalSkin / totalPixels;
  
  // Center region skin concentration (middle 50% of image)
  const centerXMin = width * 0.25;
  const centerXMax = width * 0.75;
  const centerYMin = height * 0.15;
  const centerYMax = height * 0.75;
  
  let centerSkin = 0;
  let centerTotal = 0;
  for (let y = centerYMin; y < centerYMax; y++) {
    for (let x = centerXMin; x < centerXMax; x++) {
      const i = (Math.floor(y) * width + Math.floor(x)) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if ((r > 95 && g > 40 && b > 20 && r > g && r > b && (r - g) > 15 && (r - b) > 15)) {
        centerSkin++;
      }
      centerTotal++;
    }
  }
  
  const centerSkinRatio = centerSkin / centerTotal;
  
  // Heuristic: face detected if center region has 5-70% skin pixels
  // Lower thresholds for iPhone and varied lighting
  const hasFace = centerSkinRatio > 0.05 && centerSkinRatio < 0.70;
  const confidence = Math.min(1, Math.max(0, (centerSkinRatio - 0.02) / 0.5));
  
  return {
    hasFace,
    confidence,
    centerX,
    centerY,
    boxWidth: cellW * 2,
    boxHeight: cellH * 2,
    skinRatio: centerSkinRatio,
  };
};

export const compareImages = (img1, img2) => {
  // Simple structural similarity using canvas pixel comparison
  // This is a fallback when face-api.js is unavailable
  
  const canvas1 = document.createElement('canvas');
  const canvas2 = document.createElement('canvas');
  const size = 64; // Downsample to 64x64 for fast comparison
  
  canvas1.width = size;
  canvas1.height = size;
  canvas2.width = size;
  canvas2.height = size;
  
  const ctx1 = canvas1.getContext('2d');
  const ctx2 = canvas2.getContext('2d');
  
  // Draw images (handle data URLs and regular images)
  const drawImage = (ctx, src, canvas) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, size, size);
        resolve();
      };
      img.onerror = () => resolve(); // Silently fail
      img.src = src;
    });
  };
  
  // Synchronous comparison using grayscale histograms
  const getGrayscaleHistogram = (data, w, h) => {
    const hist = new Array(256).fill(0);
    for (let i = 0; i < data.length; i += 4) {
      const gray = Math.round(data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
      hist[gray]++;
    }
    return hist;
  };
  
  const compareHistograms = (h1, h2) => {
    let sum1 = 0, sum2 = 0;
    let sumSq1 = 0, sumSq2 = 0;
    let sumProd = 0;
    const n = 256;
    
    for (let i = 0; i < n; i++) {
      sum1 += h1[i];
      sum2 += h2[i];
      sumSq1 += h1[i] * h1[i];
      sumSq2 += h2[i] * h2[i];
      sumProd += h1[i] * h2[i];
    }
    
    // Cosine similarity
    const dot = sumProd;
    const mag1 = Math.sqrt(sumSq1);
    const mag2 = Math.sqrt(sumSq2);
    
    if (mag1 === 0 || mag2 === 0) return 0;
    return dot / (mag1 * mag2);
  };
  
  return {
    hasFace: true, // Always return true - comparison is done server-side
    confidence: 0.5, // Neutral
  };
};